import {Stack, Typography} from '@mui/material';
import Box from '@mui/material/Box';
import type * as React from 'react';
import {useMemo, useState} from 'react';
import AddEmployeeCapacityAutoComplete from './AddEmployeeCapacityAutoComplete';
import {cutoffDay} from '@/components/Capacity/CapacityHours.tsx';
import MyCapacityDefaultsTable from '@/components/Capacity/MyCapacityDefaults';
import {useGetCapacityLabels} from '@/hooks/capacity/useGetCapacityLabels';
import type {CapacityDefault, CapacityDefaultWithLabel} from '@/types/CapacityDefault';
import type {Employee} from '@/types/Employee';

type Props = {
    employees : Employee[];
    capacityYearMonth : string;
    employeeId : number;
    capacityDefaults ?: CapacityDefault[];
};

const MyCapacityDefaultBuilder = ({
    employeeId, employees, capacityYearMonth, capacityDefaults,
} : Props) : React.ReactElement => {
    const employee = useMemo(() => {
        return employees.find(employee => employee.id === employeeId);
    }, [employees, employeeId]);
    const [open, setOpen] = useState(false);

    const getCapacityLabelsQuery = useGetCapacityLabels();

    const capacityDefaultsWithLabels : CapacityDefaultWithLabel[] = useMemo(() => {
        const defaults = capacityDefaults;
        const labels = getCapacityLabelsQuery.data;

        if (!defaults || !labels) {
            return [];
        }

        return defaults.map(capacityDefault => {
            return {
                ...capacityDefault,
                label: labels.find(
                    label => label.timeLabelID === capacityDefault.timeLabelID
                ),
            };
        }).sort((a, b) => {
            if (!a.label) {
                return -1;
            }

            if (!b.label) {
                return 1;
            }

            return a.label.name.localeCompare(b.label.name);
        });
    }, [capacityDefaults, getCapacityLabelsQuery.data]);

    return <Box
        sx={{
            mx: 3,
            marginTop: '80px',
        }}
    >
        <Stack
            sx={{
                flexDirection: 'row',
                width: '100%',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexGrow: 1,
            }}
        >
            <Stack
                sx={{
                    flexDirection: 'column',
                    flexGrow: 1,
                    m: 2,
                }}
            >
                <Box
                    sx={{
                        border: '1px solid black',
                        flexGrow: 1,
                        p: 1,
                    }}
                >
                    <Typography sx={{fontSize: '24px'}}>My Typical Month</Typography>
                    <Typography sx={{fontSize: '14px'}}>
                        Determine monthly internal time commitments by creating typical categories of your choice.
                    </Typography>

                    {employee && getCapacityLabelsQuery.data && <AddEmployeeCapacityAutoComplete
                        employee={employee}
                        capacityLabels={getCapacityLabelsQuery.data.filter(capacityLabel => !capacityLabel.locked)}
                        open={open}
                        setOpen={setOpen}
                        capacityYearMonth={capacityYearMonth}
                    />}
                    {capacityDefaults && getCapacityLabelsQuery.data && <MyCapacityDefaultsTable
                        data={capacityDefaultsWithLabels.filter(capacity => !capacity.label?.locked)}
                    />}
                </Box>
            </Stack>
            <Stack
                sx={{
                    flexDirection: 'column',
                    flexGrow: 1,
                    width: '40%',
                    m: 2,
                    display: {xs: 'none', md: 'inline'},
                }}
            >
                <Box
                    sx={{
                        border: '1px solid black',
                        p: 1,
                        flexGrow: 1,
                    }}
                >
                    <Typography
                        sx={{
                            mb: 2,
                            textAlign: 'center',
                            fontWeight: 800,
                        }}
                    >
                        Welcome to the Capacity Tracker Tool!
                    </Typography>

                    <Typography
                        sx={{
                            mb: 2,
                        }}
                    >
                        Input the number of hours you expect to need for meetings and other internal obligations, as
                        well as any planned time out for vacation. Weekends, holidays, and our annual offsite are
                        already accounted for in the fiscal calendar, so you can ignore those.
                    </Typography>

                    <Typography
                        sx={{
                            mb: 2,
                        }}
                    >
                        For your awareness, the general math is (hours in fiscal month) – (vacation & conference time) –
                        (prorated internal time commitments). You do NOT need to reduce internal time commitments
                        because of vacation/conference time; the tool does this for you by prorating internal
                        obligations for the portion of the month that you’ll be working. (In other words, vacation time
                        also reduces your internal obligation time, not just the time you have available for client
                        work.)
                    </Typography>

                    <Typography
                        sx={{
                            mb: 2,
                        }}
                    >
                        Update your capacity for next month by the end of the day on the {cutoffDay - 1}th of the
                        current month. Next month's capacity cannot be modified after midnight on the {cutoffDay - 1}th.
                    </Typography>

                    <Typography
                        sx={{
                            mb: 2,
                        }}
                    >
                        Capacity values will be replacing previous utilization targets for reporting and planning
                        purposes.
                    </Typography>
                </Box>
            </Stack>
        </Stack>
    </Box>;
};

export default MyCapacityDefaultBuilder;
